import { useSearchParams } from "react-router-dom";
import { RULE_FILTER_LABELS } from "@/utils/alertCopy.ts";
import type { AlertFilters, AlertPeriodDays } from "@/types";

export const ALERT_PERIODS: readonly AlertPeriodDays[] = [7, 30, 90];
export const DEFAULT_ALERT_PERIOD: AlertPeriodDays = 30;

function readPeriod(raw: string | null): AlertPeriodDays {
  const value = Number(raw);
  return ALERT_PERIODS.find((period) => period === value) ?? DEFAULT_ALERT_PERIOD;
}

function readRule(raw: string | null): string | null {
  return raw !== null && Object.hasOwn(RULE_FILTER_LABELS, raw) ? raw : null;
}

/** Alerts filters and the open alert, kept in the URL so every view is linkable. */
export function useAlertsUrlState() {
  const [params, setParams] = useSearchParams();

  const update = (change: (next: URLSearchParams) => void, replace: boolean) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        change(next);
        return next;
      },
      { replace },
    );

  const filters: AlertFilters = { rule: readRule(params.get("rule")), period_days: readPeriod(params.get("period")) };

  return {
    filters,
    alertId: params.get("alert") || null,
    setRule: (rule: string | null) => update((next) => (rule ? next.set("rule", rule) : next.delete("rule")), true),
    setPeriod: (days: AlertPeriodDays) =>
      update((next) => (days === DEFAULT_ALERT_PERIOD ? next.delete("period") : next.set("period", String(days))), true),
    openAlert: (id: string) => update((next) => next.set("alert", id), false),
    closeAlert: () => update((next) => next.delete("alert"), false),
  };
}
